import type { DeleteObligationMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  DeleteObligationDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export const useDeleteObligation = createMutationHook({
  name: 'deleteObligation',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.obligation.delete.mutationOptions({ onSuccess }),
  mapInput: (id: string) => ({ Ids: [id] }),
  invalidate: (trpc) => [
    trpc.frontend.obligation.register.queryKey(),
    trpc.frontend.obligation.getById.queryKey(),
    trpc.frontend.obligation.obligationsByType.queryKey(),
    trpc.frontend.obligation.obligationList.queryKey(),
    // COI-471 parity: Apollo twin also evicts `obligation_impact`.
    trpc.frontend.obligationImpact.pathKey(),
  ],
  // The GraphQL `deleteObligation` mutation reported two affected-row counts
  // (`delete_obligation_impact` + `delete_obligation`). The tRPC backend is a
  // single bulk delete returning one affected-count for the obligation rows;
  // the impact cascade happens server-side. We surface a single deleted
  // obligation (the hook exposes a single-Id API) and a zero impact count,
  // matching the legacy shape consumers branch on (`delete_obligation`'s
  // `affected_rows === 1`).
  mapResponse: (): DeleteObligationMutation => ({
    delete_obligation_impact: {
      __typename: 'obligation_impact_mutation_response',
      affected_rows: 0,
    },
    delete_obligation: {
      __typename: 'obligation_mutation_response',
      affected_rows: 1,
    },
  }),
  graphqlDocument: DeleteObligationDocument,
  graphqlVariables: (id: string) => ({ id }),
  evictFields: ['obligation', 'obligation_impact'],
  refetchQueries: [
    namedOperations.Query.getObligationById,
    namedOperations.Query.getObligationsByType,
  ],
});
